import React, { useState, useEffect, useRef, useMemo } from 'react';
import { leadDocumentTypeService } from '../../../api/crm/config/configService';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS } from '../../../api/config';
import { post, del } from '../../../api/apiClient';
import {
    ArrowLeft,
    Save,
    User,
    Building,
    Mail,
    Phone,
    FileText,
    MapPin,
    CheckCircle,
    XCircle,
    AlertCircle,
    Upload,
    X,
    Plus,
    Trash2,
    Link as LinkIcon,
    File,
    Calendar,
    Hash,
    Eye,
    Users,
    AlertTriangle,
    ExternalLink
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import customerService from '../../../api/crm/customers/customerService';
import fieldSalesService from '../../../api/crm/field-sales/fieldSalesService';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import {
    sanitizeTextInput,
    sanitizeEmailInput,
    sanitizeAddressInput,
    validatePhoneInput,
    validateEmailInput,
    validateRequiredFields,
    getCharacterCount,
    FIELD_LIMITS
} from '../../../utils/inputValidation';
import { toast } from 'react-hot-toast';
import { Country, State, City } from 'country-state-city';
import LocationSelect from '../../../components/common/LocationSelect/LocationSelect';
import { useAuth } from '../../../context/AuthContext';

const CustomerForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const isEditMode = !!id;
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        profileImage: null,
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        alternatePhone: '',
        website: '',
        industry: '',
        companySize: '',
        annualRevenue: '',
        region: '',
        decisionMaker: '',
        businessType: 'other',
        source: 'direct',
        status: 'customer',
        priority: 'medium',
        assignedTo: '',
        creditLimit: 0,
        paymentTerms: 'credit_30',
        preferredChannel: 'email',
        tags: [],
        isActive: true,
        billingAddress: {
            street: '',
            city: null,
            state: null,
            pincode: '',
            country: { name: 'India', isoCode: 'IN' }
        },
        shippingAddress: {
            street: '',
            city: null,
            state: null,
            pincode: '',
            country: { name: 'India', isoCode: 'IN' }
        }
    });

    const [previewImage, setPreviewImage] = useState(null);

    // Document upload state (ManualLeadEntry style)
    const [customerDocuments, setCustomerDocuments] = useState([]);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [isLoadingDocTypes, setIsLoadingDocTypes] = useState(false);
    const [isUploadingDoc, setIsUploadingDoc] = useState(false);
    const [draggingDocIdx, setDraggingDocIdx] = useState(null);

    const [shippingSameAsBilling, setShippingSameAsBilling] = useState(!isEditMode);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    // Location options states
    const [billingCountryOptions, setBillingCountryOptions] = useState([]);
    const [billingStateOptions, setBillingStateOptions] = useState([]);
    const [billingCityOptions, setBillingCityOptions] = useState([]);

    const [shippingCountryOptions, setShippingCountryOptions] = useState([]);
    const [shippingStateOptions, setShippingStateOptions] = useState([]);
    const [shippingCityOptions, setShippingCityOptions] = useState([]);

    // Employee options for assignment
    const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
    const [selectedAssignee, setSelectedAssignee] = useState(null);
    const [duplicateWarning, setDuplicateWarning] = useState(null);
    const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);

    // Load countries on mount
    useEffect(() => {
        const allCountries = Country.getAllCountries().map(c => ({
            id: c.isoCode,
            name: c.name,
            isoCode: c.isoCode,
            ...c
        }));
        setBillingCountryOptions(allCountries);
        setShippingCountryOptions(allCountries);
        // Fetch document types
        setIsLoadingDocTypes(true);
        leadDocumentTypeService.getAll({ isActive: true })
            .then(res => {
                setDocumentTypes(res?.data || []);
            })
            .catch(() => setDocumentTypes([]))
            .finally(() => setIsLoadingDocTypes(false));
    }, []);

    const isSalesExecutive = user?.role === 'sales_executive' || user?.role === 'export_executive' || user?.role === 'business_relationship_manager' || user?.role === 'business_development_executive';

    // Auto-assign to self if CRM staff (sales/export manager or executive) and creating new customer
    useEffect(() => {
        const isCRMStaff = ['sales_executive', 'export_executive', 'sales_manager', 'export_manager', 'business_relationship_manager', 'business_development_executive'].includes(user?.role);
        if (!isEditMode && isCRMStaff && (user?.id || user?._id) && !formData.assignedTo) {
            setFormData(prev => ({
                ...prev,
                assignedTo: user.id || user._id
            }));
        }
    }, [isEditMode, user, formData.assignedTo]);

    const {
        data: assigneesData,
        isLoading: loadingAssignees
    } = useQuery({
        queryKey: ['fieldSales', 'salesReps', assigneeSearchTerm],
        queryFn: async () => {
            const params = {
                limit: 10,
                role: ['sales_executive', 'sales_manager', 'export_executive', 'export_manager', 'business_relationship_manager', 'business_development_executive'],
                ...(assigneeSearchTerm && assigneeSearchTerm.length >= 2 ? { search: assigneeSearchTerm } : {})
            };
            const response = await fieldSalesService.getSalesReps(params);
            if (response.success && response.data) {
                return response.data;
            } else {
                throw new Error(response.error || 'Failed to load assignees');
            }
        }
    });

    // Sync Billing States
    useEffect(() => {
        if (formData.billingAddress.country?.isoCode) {
            const states = State.getStatesOfCountry(formData.billingAddress.country.isoCode).map(s => ({
                id: s.isoCode,
                name: s.name,
                isoCode: s.isoCode,
                countryCode: s.countryCode,
                ...s
            }));
            setBillingStateOptions(states);
        } else {
            setBillingStateOptions([]);
        }
    }, [formData.billingAddress.country]);

    // Sync Billing Cities
    useEffect(() => {
        if (formData.billingAddress.country?.isoCode && formData.billingAddress.state?.isoCode) {
            const cities = City.getCitiesOfState(formData.billingAddress.country.isoCode, formData.billingAddress.state.isoCode).map(c => ({
                id: `${c.name}-${c.stateCode}`,
                name: c.name,
                stateCode: c.stateCode,
                countryCode: c.countryCode,
                ...c
            }));
            setBillingCityOptions(cities);
        } else {
            setBillingCityOptions([]);
        }
    }, [formData.billingAddress.country, formData.billingAddress.state]);

    // Sync Shipping States
    useEffect(() => {
        if (formData.shippingAddress.country?.isoCode) {
            const states = State.getStatesOfCountry(formData.shippingAddress.country.isoCode).map(s => ({
                id: s.isoCode,
                name: s.name,
                isoCode: s.isoCode,
                countryCode: s.countryCode,
                ...s
            }));
            setShippingStateOptions(states);
        } else {
            setShippingStateOptions([]);
        }
    }, [formData.shippingAddress.country]);

    // Sync Shipping Cities
    useEffect(() => {
        if (formData.shippingAddress.country?.isoCode && formData.shippingAddress.state?.isoCode) {
            const cities = City.getCitiesOfState(formData.shippingAddress.country.isoCode, formData.shippingAddress.state.isoCode).map(c => ({
                id: `${c.name}-${c.stateCode}`,
                name: c.name,
                stateCode: c.stateCode,
                countryCode: c.countryCode,
                ...c
            }));
            setShippingCityOptions(cities);
        } else {
            setShippingCityOptions([]);
        }
    }, [formData.shippingAddress.country, formData.shippingAddress.state]);

    // Fetch customer data if in edit mode
    const { data: customerData, isLoading: isFetching } = useQuery({
        queryKey: ['customer', id],
        queryFn: async () => {
            if (!id) return null;
            const result = await customerService.getCustomerById(id);
            if (result.success) return result.customer;
            throw new Error(result.error);
        },
        enabled: isEditMode
    });

    // Fetch industries configuration
    const { data: industriesData, isLoading: isLoadingIndustries } = useQuery({
        queryKey: ['industries'],
        queryFn: async () => {
            const result = await customerService.getIndustries();
            if (result.success) return result.data;
            return [];
        }
    });

    // Fetch decision makers configuration
    const { data: decisionMakersData, isLoading: isLoadingDecisionMakers } = useQuery({
        queryKey: ['decisionMakers'],
        queryFn: async () => {
            const result = await customerService.getDecisionMakers();
            if (result.success) return result.data;
            return [];
        }
    });

    const assigneeOptions = useMemo(() => {
        const reps = [...(assigneesData || [])];

        // Ensure the current assigned user is in the options list (creation or edit mode)
        if (formData.assignedTo) {
            const exists = reps.some(rep =>
                rep._id === formData.assignedTo ||
                rep.userId === formData.assignedTo ||
                rep.user === formData.assignedTo
            );
            if (!exists) {
                if (selectedAssignee && (
                    selectedAssignee._id === formData.assignedTo ||
                    selectedAssignee.userId === formData.assignedTo ||
                    selectedAssignee.user === formData.assignedTo ||
                    selectedAssignee.id === formData.assignedTo
                )) {
                    reps.unshift(selectedAssignee);
                } else if (user && (user.id === formData.assignedTo || user._id === formData.assignedTo)) {
                    reps.unshift({
                        _id: user._id, // Employee ID
                        userId: user.id, // User ID
                        user: user.id,
                        employeeId: user.employeeId || user.empId || '',
                        firstName: user.firstName || '',
                        lastName: user.lastName || '',
                        email: user.email,
                        role: user.role,
                        department: user.department
                    });
                } else if (isEditMode && customerData) {
                    const customer = customerData.customer || customerData;
                    const currentAssigned = customer.assignedTo;
                    if (currentAssigned && typeof currentAssigned === 'object') {
                        const assignedId = currentAssigned._id || currentAssigned.id;
                        if (assignedId === formData.assignedTo) {
                            reps.unshift(currentAssigned);
                        }
                    }
                }
            }
        }

        return reps.map(rep => {
            const roleStr = rep.role ? rep.role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown';
            const name = rep.name || `${rep.firstName || ''} ${rep.lastName || ''}`.trim() || rep.email || 'Unknown';
            const empCode = rep.employeeId || rep.empId || (rep.employee && typeof rep.employee === 'object' ? rep.employee.employeeId : null) || 'N/A';
            return {
                id: rep.userId || rep.user || rep._id,
                displayText: `${name} [${roleStr}]`,
                searchText: `${name} ${rep.email || ''} ${empCode !== 'N/A' ? empCode : ''} ${rep.role || ''} ${rep.department || ''}`.toLowerCase(),
                title: name,
                rep: rep,
                renderOption: (opt) => (
                    <div className="flex flex-col py-0.5">
                        <span className="font-semibold text-gray-900">{opt.title}</span>
                        <div className="flex items-center text-[11px] mt-0.5 space-x-2 text-gray-500">
                            <span className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 uppercase tracking-wider font-bold" style={{ fontSize: '9px' }}>
                                {roleStr}
                            </span>
                            <span>•</span>
                            <span className="font-medium">{empCode}</span>
                            {rep.department && (
                                <>
                                    <span>•</span>
                                    <span>{rep.department}</span>
                                </>
                            )}
                        </div>
                    </div>
                )
            };
        });
    }, [assigneesData, isEditMode, customerData, formData.assignedTo, user, selectedAssignee]);

    // Populate form data when customer data is fetched
    useEffect(() => {
        if (customerData) {
            const customer = customerData.customer || customerData;
            if (customer.assignedTo && typeof customer.assignedTo === 'object') {
                setSelectedAssignee(customer.assignedTo);
            }
            setFormData(prev => ({
                ...prev,
                profileImage: customer.profileImage || null,
                companyName: customer.companyName || '',
                contactPerson: customer.contactPerson || '',
                email: customer.email || '',
                phone: Array.isArray(customer.phone) ? (customer.phone[0] || '') : (customer.phone || ''),
                alternatePhone: Array.isArray(customer.phone) ? (customer.phone[1] || '') : '',
                website: customer.website || '',
                industry: customer.industry || '',
                companySize: customer.companySize || '',
                annualRevenue: customer.annualRevenue ? String(customer.annualRevenue) : '',
                region: customer.region || '',
                decisionMaker: customer.decisionMaker || '',
                businessType: customer.businessType || 'other',
                source: customer.source || 'direct',
                status: customer.status || 'customer',
                priority: customer.priority || 'medium',
                assignedTo: customer.assignedTo?._id || customer.assignedTo || '',
                creditLimit: customer.creditLimit || 0,
                paymentTerms: customer.paymentTerms || 'credit_30',
                preferredChannel: customer.preferredChannel || 'email',
                tags: customer.tags || [],
                isActive: customer.isActive !== false,
                billingAddress: {
                    street: customer.billingAddress?.street || '',
                    city: customer.billingAddress?.city?.name ? customer.billingAddress.city : (customer.billingAddress?.city ? { name: customer.billingAddress.city } : null),
                    state: customer.billingAddress?.state?.name ? customer.billingAddress.state : (customer.billingAddress?.state ? { name: customer.billingAddress.state } : null),
                    pincode: customer.billingAddress?.pincode || '',
                    country: customer.billingAddress?.country?.isoCode ? customer.billingAddress.country : (customer.billingAddress?.country ? { name: customer.billingAddress.country, isoCode: 'IN' } : { name: 'India', isoCode: 'IN' })
                },
                shippingAddress: {
                    street: customer.shippingAddress?.street || '',
                    city: customer.shippingAddress?.city?.name ? customer.shippingAddress.city : (customer.shippingAddress?.city ? { name: customer.shippingAddress.city } : null),
                    state: customer.shippingAddress?.state?.name ? customer.shippingAddress.state : (customer.shippingAddress?.state ? { name: customer.shippingAddress.state } : null),
                    pincode: customer.shippingAddress?.pincode || '',
                    country: customer.shippingAddress?.country?.isoCode ? customer.shippingAddress.country : (customer.shippingAddress?.country ? { name: customer.shippingAddress.country, isoCode: 'IN' } : { name: 'India', isoCode: 'IN' })
                }
            }));
            if (customer.profileImage && !previewImage) {
                const preview = typeof customer.profileImage === 'string'
                    ? customer.profileImage
                    : (customer.profileImage.fileUrl || customer.profileImage.url);
                if (preview) setPreviewImage(preview);
            }

            // Populate documents
            const rawDocs = customer.customerDocuments || customer.documents || [];
            if (Array.isArray(rawDocs) && rawDocs.length > 0) {
                setCustomerDocuments(rawDocs.map(doc => {
                    const docTypeId = typeof doc.documentTypeId === 'object'
                        ? (doc.documentTypeId?._id || doc.documentTypeId?.id || '')
                        : (doc.documentTypeId || doc.type || (typeof doc.type === 'object' ? doc.type?._id : ''));
                    const docTypeName = doc.documentTypeName
                        || (typeof doc.documentTypeId === 'object' ? doc.documentTypeId?.name : '')
                        || (typeof doc.type === 'object' ? doc.type?.name : '');

                    const rawExpiry = doc.expiryDate || doc.expiry || '';
                    const formattedExpiry = rawExpiry
                        ? (typeof rawExpiry === 'string' ? rawExpiry.split('T')[0] : new Date(rawExpiry).toISOString().split('T')[0])
                        : '';

                    let fileObj = doc.fileInfo || null;
                    if (!fileObj && (doc.url || doc.documentLink)) {
                        const fileUrl = doc.url || doc.documentLink;
                        fileObj = {
                            url: fileUrl,
                            name: fileUrl.split('/').pop() || 'Document',
                            originalName: fileUrl.split('/').pop() || 'Document',
                            size: 0,
                            mimetype: ''
                        };
                    } else if (fileObj) {
                        fileObj = {
                            ...fileObj,
                            name: fileObj.name || fileObj.originalName || (fileObj.url ? fileObj.url.split('/').pop() : 'Document'),
                            originalName: fileObj.originalName || fileObj.name || (fileObj.url ? fileObj.url.split('/').pop() : 'Document'),
                            url: fileObj.url || fileObj.secure_url || ''
                        };
                    }

                    return {
                        documentTypeId: String(docTypeId || ''),
                        documentTypeName: docTypeName || '',
                        certificateNumber: doc.certificateNumber || doc.number || '',
                        expiryDate: formattedExpiry,
                        documentMethod: doc.documentMethod || (doc.documentLink || doc.url ? 'link' : 'upload'),
                        documentLink: doc.documentLink || doc.url || '',
                        file: null,
                        fileInfo: fileObj,
                        uploading: false
                    };
                }));
            }


            if (customer.billingAddress && customer.shippingAddress) {
                const isSame = JSON.stringify(customer.billingAddress) === JSON.stringify(customer.shippingAddress);
                setShippingSameAsBilling(isSame);
            }
        }
    }, [customerData]);

    // Sync shipping with billing if checkbox is checked
    useEffect(() => {
        if (shippingSameAsBilling) {
            setFormData(prev => ({
                ...prev,
                shippingAddress: { ...prev.billingAddress }
            }));
        }
    }, [shippingSameAsBilling, formData.billingAddress]);

    const handleDuplicateCheck = async (field, value) => {
        if (!value || isEditMode) return;

        // Basic validation before check to avoid unnecessary API calls
        if (field === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return;
        if (field === 'phone' && value.replace(/\D/g, '').length < 10) return;

        setIsCheckingDuplicate(true);
        try {
            const params = field === 'email' ? { email: value } : { phone: value };
            const result = await customerService.checkDuplicate(params);

            if (result.success && result.isDuplicate) {
                setDuplicateWarning({
                    field: field === 'email' ? 'Email' : 'Phone Number',
                    value: value,
                    customer: result.customer
                });
            }
        } catch (error) {
        } finally {
            setIsCheckingDuplicate(false);
        }
    };

    const handleFieldChange = (field, value, fieldType = 'text', maxLength = FIELD_LIMITS[field] || 255) => {
        let processedValue = value;
        let error = '';

        if (fieldType === 'select') {
            // For select/dropdown, use value directly without any processing
            processedValue = value;
        } else if (fieldType === 'url') {
            // For URL fields, allow all valid URL characters
            // Valid URL characters: A-Z a-z 0-9 - . _ ~ : / ? # [ ] @ ! $ & ' ( ) * + , ; = %
            processedValue = String(value || '').slice(0, maxLength);
        } else if (fieldType === 'plainText') {
            // Customer name fields: allow any characters, no length limit
            processedValue = String(value || '');
        } else if (fieldType === 'text') {
            processedValue = sanitizeTextInput(String(value || ''));
            processedValue = processedValue.slice(0, maxLength);
        } else if (fieldType === 'phone') {
            const phoneValidation = validatePhoneInput(value);
            processedValue = phoneValidation.value;
            error = phoneValidation.error || '';
        } else if (fieldType === 'email') {
            processedValue = sanitizeEmailInput(String(value || ''));
            processedValue = processedValue.slice(0, maxLength);
            if (processedValue) {
                const emailValidation = validateEmailInput(processedValue);
                if (!emailValidation.isValid) {
                    error = emailValidation.error;
                }
            }
        } else if (fieldType === 'address') {
            processedValue = sanitizeAddressInput(String(value || ''));
            processedValue = processedValue.slice(0, maxLength);
        } else if (fieldType === 'number') {
            // Only allow numbers
            processedValue = String(value || '').replace(/\D/g, '');
            processedValue = processedValue.slice(0, maxLength);
        }

        // Handle nested paths like 'billingAddress.city'
        if (field.includes('.')) {
            const [parent, child] = field.split('.');

            // If user edits shipping address manually, uncheck "Copy from billing"
            if (parent === 'shippingAddress' && shippingSameAsBilling) {
                setShippingSameAsBilling(false);
            }
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: processedValue
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: processedValue
            }));
        }

        // Set error if validation failed
        if (error) {
            setErrors(prev => ({
                ...prev,
                [field]: error
            }));
        } else if (errors[field]) {
            // Clear error if validation passed
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleProfileImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload a valid image file (JPG, PNG, GIF, or WEBP)');
            return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            toast.error('Image size must be less than 5MB');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const imageData = {
                file: file, // Store the raw file object
                fileUrl: event.target.result,
                fileType: file.type,
                fileSize: file.size,
                originalName: file.name,
                uploadedAt: new Date()
            };
            setFormData(prev => ({ ...prev, profileImage: imageData }));
            setPreviewImage(event.target.result);
        };
        reader.readAsDataURL(file);
    };

    // Document section handlers (ManualLeadEntry style)
    const handleAddDocument = () => {
        setCustomerDocuments(prev => ([
            ...prev,
            {
                documentTypeId: '',
                documentTypeName: '',
                certificateNumber: '',
                expiryDate: '',
                documentMethod: 'upload',
                documentLink: '',
                file: null,
                fileInfo: null,
                uploading: false
            }
        ]));
    };

    const handleRemoveDocument = (idx) => {
        setCustomerDocuments(prev => prev.filter((_, i) => i !== idx));
    };

    const handleDocumentChange = (idx, field, value) => {
        setCustomerDocuments(prev => prev.map((doc, i) => {
            if (i !== idx) return doc;
            const updated = { ...doc, [field]: value };
            // If switching method, clear the other
            if (field === 'documentMethod') {
                if (value === 'upload') {
                    updated.documentLink = '';
                } else {
                    updated.file = null;
                    updated.fileInfo = null;
                }
            }
            return updated;
        }));
    };

    // Mutations for Cloudinary
    const uploadDocMutation = useMutation({
        mutationFn: async ({ file, idx }) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('folder', 'customer-documents');
            const result = await post(API_ENDPOINTS.UPLOAD.CLOUDINARY, formData);
            if (result.responseCode !== 200) throw new Error(result.responseMessage || 'Upload failed');
            return { result, idx, file };
        },
        onSuccess: ({ result, idx, file }) => {
            setCustomerDocuments(prev => prev.map((doc, i) => i === idx ? {
                ...doc,
                file: null,
                fileInfo: {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    url: result.data.url,
                    public_id: result.data.public_id
                },
                uploading: false
            } : doc));
            toast.success('Document uploaded successfully');
        },
        onError: (error, { idx }) => {
            setCustomerDocuments(prev => prev.map((doc, i) => i === idx ? { ...doc, uploading: false } : doc));
            toast.error(error.message || 'Upload failed');
        }
    });

    const deleteDocMutation = useMutation({
        mutationFn: async ({ publicId, idx }) => {
            if (!publicId) return { idx };
            const result = await del(`${API_ENDPOINTS.UPLOAD.CLOUDINARY}?public_id=${publicId}&resource_type=raw`);
            if (result.responseCode !== 200) throw new Error(result.responseMessage || 'Delete failed');
            return { idx };
        },
        onSuccess: ({ idx }) => {
            setCustomerDocuments(prev => prev.map((doc, i) => i === idx ? { ...doc, file: null, fileInfo: null } : doc));
            toast.success('Document removed successfully');
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to remove document');
        }
    });

    const handleDocumentFile = (idx, e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setCustomerDocuments(prev => prev.map((doc, i) => i === idx ? { ...doc, uploading: true } : doc));
        uploadDocMutation.mutate({ file, idx });
    };

    const handleDocumentDelete = (idx) => {
        const doc = customerDocuments[idx];
        if (doc.fileInfo?.public_id) {
            deleteDocMutation.mutate({ publicId: doc.fileInfo.public_id, idx });
        } else {
            handleDocumentChange(idx, 'file', null);
            handleDocumentChange(idx, 'fileInfo', null);
        }
    };

    const handleDragOver = (e, idx) => {
        e.preventDefault();
        setDraggingDocIdx(idx);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        setDraggingDocIdx(null);
    };
    const handleDrop = (e, idx) => {
        e.preventDefault();
        setDraggingDocIdx(null);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const mockEvent = { target: { files: e.dataTransfer.files } };
            handleDocumentFile(idx, mockEvent);
        }
    };

    const handleBlur = (field) => {
        const value = formData[field];
        let errorMessage = '';

        // Check required fields
        if (['contactPerson', 'phone'].includes(field)) {
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                const fieldLabel = field === 'contactPerson' ? 'Customer Name' : field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1').trim();
                errorMessage = `${fieldLabel} is required`;
            }
        }

        // Check format validations if value exists
        if (value && !errorMessage) {
            if (field === 'email') {
                const emailValidation = validateEmailInput(value);
                if (!emailValidation.isValid) {
                    errorMessage = emailValidation.error;
                }
            } else if (field === 'phone') {
                const phoneValidation = validatePhoneInput(value);
                if (!phoneValidation.isValid) {
                    errorMessage = phoneValidation.error || 'Phone number must be 10 digits';
                }
            } else if (field === 'pan') {
                const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
                if (value.length !== 10) {
                    errorMessage = 'PAN number must be exactly 10 characters';
                } else if (!panRegex.test(value)) {
                    errorMessage = 'Invalid PAN format (e.g., ABCDE1234F)';
                }
            }
        }

        if (errorMessage) {
            setErrors(prev => ({
                ...prev,
                [field]: errorMessage
            }));
        } else {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });

            // If no validation error, check for duplicates in the backend
            if (['email', 'phone'].includes(field) && !isEditMode && value) {
                handleDuplicateCheck(field, value);
            }
        }
    };

    const removeProfileImage = () => {
        setFormData(prev => ({ ...prev, profileImage: null }));
        setPreviewImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const validateFormData = () => {
        const newErrors = {};

        // Validate required fields
        const requiredFields = ['contactPerson', 'phone'];
        requiredFields.forEach(field => {
            const value = formData[field];
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                const fieldLabel = field === 'contactPerson' ? 'Customer Name' : field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1').trim();
                newErrors[field] = `${fieldLabel} is required`;
            }
        });

        // Validate email format if provided
        if (formData.email) {
            const emailValidation = validateEmailInput(formData.email);
            if (!emailValidation.isValid) {
                newErrors.email = emailValidation.error;
            }
        }

        // Validate phone number (must be 10 digits)
        if (formData.phone) {
            const phoneValidation = validatePhoneInput(formData.phone);
            if (!phoneValidation.isValid) {
                newErrors.phone = phoneValidation.error || 'Phone number must be 10 digits';
            }
        }

        // ...existing code, remove PAN validation...

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError(null);

        // Validate form data
        if (!validateFormData()) {
            toast.error('Please fill all the required fields');
            return;
        }

        setIsSubmitting(true);
        try {
            const formattedBilling = {
                street: formData.billingAddress.street || 'Not provided',
                city: formData.billingAddress.city || { name: 'Not provided' },
                state: formData.billingAddress.state || { name: 'Not provided' },
                country: formData.billingAddress.country || { name: 'India', isoCode: 'IN' },
                pincode: formData.billingAddress.pincode || '000000'
            };

            const formattedShipping = {
                street: formData.shippingAddress.street || 'Not provided',
                city: formData.shippingAddress.city || { name: 'Not provided' },
                state: formData.shippingAddress.state || { name: 'Not provided' },
                country: formData.shippingAddress.country || { name: 'India', isoCode: 'IN' },
                pincode: formData.shippingAddress.pincode || '000000'
            };

            const phoneArray = [formData.phone, formData.alternatePhone].map(p => p?.trim()).filter(Boolean);

            const baseData = {
                ...formData,
                assignedTo: formData.assignedTo || null,
                phone: phoneArray, // Store as array
                billingAddress: formattedBilling,
                shippingAddress: shippingSameAsBilling ? formattedBilling : formattedShipping,
                documents: customerDocuments.map(doc => ({
                    documentTypeId: doc.documentTypeId,
                    documentTypeName: doc.documentTypeName,
                    certificateNumber: doc.certificateNumber,
                    expiryDate: doc.expiryDate,
                    documentMethod: doc.documentMethod,
                    documentLink: doc.documentMethod === 'link' ? doc.documentLink : (doc.fileInfo?.url || ''),
                    fileInfo: doc.fileInfo || null
                }))
            };

            // Cleanup internal state before sending if needed (optional)
            delete baseData.alternatePhone;

            let result;
            // Check if we have any files to upload (profile image or documents)
            const hasDocumentFiles = customerDocuments.some(doc => !!doc.file);
            const hasProfileImageFile = !!formData.profileImage?.file;

            if (hasProfileImageFile || hasDocumentFiles) {
                const formDataToSubmit = new FormData();

                if (hasProfileImageFile) {
                    formDataToSubmit.append('profileImage', formData.profileImage.file);
                }

                // Remove the file object and fileUrl from data we send as JSON
                const { profileImage, ...otherData } = baseData;
                formDataToSubmit.append('data', JSON.stringify(otherData));

                // Attach document files
                customerDocuments.forEach((doc) => {
                    if (doc.file) {
                        formDataToSubmit.append(`documentFiles`, doc.file);
                    }
                });

                if (isEditMode) {
                    result = await customerService.updateCustomer(id, formDataToSubmit);
                } else {
                    result = await customerService.createCustomer(formDataToSubmit);
                }
            } else {
                // Regular JSON submission (if no new files)
                if (isEditMode) {
                    result = await customerService.updateCustomer(id, baseData);
                } else {
                    result = await customerService.createCustomer(baseData);
                }
            }

            if (result.success) {
                toast.success(isEditMode ? 'Customer updated successfully!' : 'Customer created successfully!');
                queryClient.invalidateQueries(['customer360List']);
                if (isEditMode) {
                    queryClient.invalidateQueries(['customer', id]);
                    queryClient.invalidateQueries(['customer360', id]);
                }
                setTimeout(() => {
                    navigate(-1);
                }, 100);
            } else {
                setSubmitError(result.error || 'Something went wrong');
                toast.error(result.error || 'Something went wrong');
            }
        } catch (err) {
            setSubmitError(err.message || 'Failed to save customer');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isEditMode && isFetching) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-agrosaf-green"></div>
            </div>
        );
    }

    return (
        <div className="py-6 px-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <ArrowLeft className="h-6 w-6 text-gray-600" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">
                            {isEditMode ? 'Edit Customer' : 'Add New Customer'}
                        </h1>
                        <p className="text-gray-600">
                            {isEditMode ? 'Update customer profile and settings' : 'Create a new customer profile'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Alert */}
                {submitError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-3">
                        <AlertCircle className="h-5 w-5" />
                        <p>{submitError}</p>
                    </div>
                )}

                {/* Basic Information Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                        <User className="h-5 w-5 text-agrosaf-green" />
                        <h2 className="font-semibold text-gray-900">Basic Information</h2>
                    </div>
                    <div className="p-6">
                        {/* Profile Image Section */}
                        <div className="mb-8 flex flex-col sm:flex-row items-center gap-6 p-4 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                            <div className="relative group">
                                <div className="h-24 w-24 rounded-full overflow-hidden bg-white border-2 border-white shadow-md ring-1 ring-gray-200">
                                    {previewImage ? (
                                        <img
                                            src={previewImage}
                                            alt="Profile Preview"
                                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                                        />
                                    ) : (
                                        <div className="h-full w-full flex items-center justify-center bg-gray-50">
                                            <User className="h-10 w-10 text-gray-300" />
                                        </div>
                                    )}
                                </div>
                                {previewImage && (
                                    <button
                                        type="button"
                                        onClick={removeProfileImage}
                                        className="absolute top-0 right-0 p-1.5 bg-red-500 text-white rounded-full shadow-lg hover:bg-red-600 transition-all opacity-0 group-hover:opacity-100"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                            <div className="flex-1 text-center sm:text-left">
                                <h3 className="text-sm font-semibold text-gray-900 mb-1">Profile Image</h3>
                                <p className="text-xs text-gray-500 mb-4">Recommended: Square image, max 5MB (JPG, PNG)</p>
                                <label className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-agrosaf-green hover:text-agrosaf-green cursor-pointer transition-all shadow-sm active:scale-95">
                                    <Upload className="h-4 w-4 mr-2" />
                                    {previewImage ? 'Change Image' : 'Upload Image'}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        className="hidden"
                                        accept="image/*"
                                        onChange={handleProfileImageUpload}
                                    />
                                </label>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Customer Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="contactPerson"
                                    value={formData.contactPerson}
                                    onChange={(e) => handleFieldChange('contactPerson', e.target.value, 'plainText')}
                                    placeholder="Enter full name"
                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent outline-none transition-all ${errors.contactPerson ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    onBlur={() => handleBlur('contactPerson')}
                                />
                                <div className="flex justify-between mt-1">
                                    {errors.contactPerson ? (
                                        <p className="text-xs text-red-500">{errors.contactPerson}</p>
                                    ) : (
                                        <div />
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Company/Business Name
                                </label>
                                <input
                                    type="text"
                                    name="companyName"
                                    value={formData.companyName}
                                    onChange={(e) => handleFieldChange('companyName', e.target.value, 'plainText')}
                                    placeholder="Enter company name"
                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent outline-none transition-all ${errors.companyName ? 'border-red-500' : 'border-gray-300'}`}
                                />
                                <div className="flex justify-between mt-1">
                                    {errors.companyName ? (
                                        <p className="text-xs text-red-500">{errors.companyName}</p>
                                    ) : (
                                        <div />
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={(e) => handleFieldChange('email', e.target.value, 'email', 100)}
                                    placeholder="example@email.com"
                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent outline-none transition-all ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                                    onBlur={() => handleBlur('email')}
                                />
                                <div className="flex justify-between mt-1">
                                    {errors.email ? (
                                        <p className="text-xs text-red-500">{errors.email}</p>
                                    ) : (
                                        <div />
                                    )}
                                    <span className="text-[10px] text-gray-400 font-medium">
                                        {getCharacterCount(formData.email, 100)}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Phone Number <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={(e) => handleFieldChange('phone', e.target.value, 'phone', 10)}
                                    placeholder="Enter 10-digit number"
                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent outline-none transition-all ${errors.phone ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    onBlur={() => handleBlur('phone')}
                                />
                                <div className="flex justify-between mt-1">
                                    {errors.phone ? (
                                        <p className="text-xs text-red-500">{errors.phone}</p>
                                    ) : (
                                        <div />
                                    )}
                                    <span className="text-[10px] text-gray-400 font-medium">
                                        {getCharacterCount(formData.phone, 15)}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Alternate Phone
                                </label>
                                <input
                                    type="text"
                                    name="alternatePhone"
                                    value={formData.alternatePhone}
                                    onChange={(e) => handleFieldChange('alternatePhone', e.target.value, 'phone', 10)}
                                    placeholder="Enter 10-digit number"
                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent outline-none transition-all ${errors.alternatePhone ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                />
                                <div className="flex justify-between mt-1">
                                    {errors.alternatePhone ? (
                                        <p className="text-xs text-red-500">{errors.alternatePhone}</p>
                                    ) : (
                                        <div />
                                    )}
                                    <span className="text-[10px] text-gray-400 font-medium">
                                        {getCharacterCount(formData.alternatePhone, 15)}
                                    </span>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                {/* Document Upload Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-green-100 rounded-lg">
                                <FileText className="h-5 w-5 text-agrosaf-green" />
                            </div>
                            <div>
                                <h2 className="font-semibold text-gray-900 leading-tight">Document Uploads</h2>
                                <p className="text-xs text-gray-500">Provide necessary identification and legal documents</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-agrosaf-green hover:bg-green-50 hover:border-green-200 transition-all shadow-sm active:scale-95"
                            onClick={handleAddDocument}
                        >
                            <Plus className="h-4 w-4" /> Add New
                        </button>
                    </div>
                    <div className="p-6">
                        {customerDocuments.length === 0 ? (
                            <div className="text-center py-10 border-2 border-dashed border-gray-100 rounded-xl bg-gray-50/30">
                                <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500 font-medium">No documents added yet</p>
                                <p className="text-xs text-gray-400 mb-4">Add documents like GST, PAN, or Licenses</p>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleAddDocument}
                                    className="!border-agrosaf-green !text-agrosaf-green hover:!bg-green-50"
                                >
                                    <Plus className="h-4 w-4 mr-2" /> Add Your First Document
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {customerDocuments.map((doc, idx) => (
                                    <div
                                        key={idx}
                                        className={`relative group bg-white border rounded-xl transition-all duration-200 ${draggingDocIdx === idx
                                            ? 'border-agrosaf-green bg-green-50/50 shadow-md transform scale-[1.01]'
                                            : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                                            }`}
                                        onDragOver={e => handleDragOver(e, idx)}
                                        onDragLeave={handleDragLeave}
                                        onDrop={e => handleDrop(e, idx)}
                                    >
                                        {/* Status Header */}
                                        <div className="flex items-center justify-between px-4 py-2 bg-gray-50/50 border-b border-gray-100 rounded-t-xl">
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Document #{idx + 1}</span>
                                            <button
                                                type="button"
                                                className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all"
                                                onClick={() => handleRemoveDocument(idx)}
                                                title="Remove Document"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>

                                        <div className="p-4">
                                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                                                {/* Type Selection */}
                                                <div className="md:col-span-3">
                                                    <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1.5">
                                                        <FileText className="h-3.5 w-3.5 text-gray-400" />
                                                        Document Type <span className="text-red-500">*</span>
                                                    </label>
                                                    <select
                                                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500/20 focus:border-agrosaf-green outline-none transition-all capitalize"
                                                        value={doc.documentTypeId || ''}
                                                        onChange={e => {
                                                            const val = e.target.value;
                                                            handleDocumentChange(idx, 'documentTypeId', val);
                                                            const selectedType = documentTypes.find(dt => String(dt._id || dt.id) === String(val));
                                                            if (selectedType) {
                                                                handleDocumentChange(idx, 'documentTypeName', selectedType.name);
                                                            }
                                                        }}
                                                        required
                                                    >
                                                        <option value="">Select Type</option>
                                                        {documentTypes.map(dt => (
                                                            <option key={dt._id || dt.id} value={dt._id || dt.id} className="capitalize">{dt.name}</option>
                                                        ))}
                                                        {doc.documentTypeId && !documentTypes.some(dt => String(dt._id || dt.id) === String(doc.documentTypeId)) && (
                                                            <option value={doc.documentTypeId} className="capitalize">
                                                                {doc.documentTypeName || doc.documentTypeId}
                                                            </option>
                                                        )}
                                                    </select>
                                                </div>

                                                {/* Document Number */}
                                                <div className="md:col-span-3">
                                                    <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1.5">
                                                        <Hash className="h-3.5 w-3.5 text-gray-400" />
                                                        Document Number
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500/20 focus:border-agrosaf-green outline-none transition-all"
                                                        value={doc.certificateNumber || ''}
                                                        onChange={e => handleDocumentChange(idx, 'certificateNumber', e.target.value)}
                                                        placeholder="e.g. GSTIN123456"
                                                        maxLength={40}
                                                    />
                                                </div>

                                                {/* Expiry Date */}
                                                <div className="md:col-span-3">
                                                    <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1.5">
                                                        <Calendar className="h-3.5 w-3.5 text-gray-400" />
                                                        Expiry Date
                                                    </label>
                                                    <input
                                                        type="date"
                                                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500/20 focus:border-agrosaf-green outline-none transition-all"
                                                        value={doc.expiryDate ? (typeof doc.expiryDate === 'string' ? doc.expiryDate.split('T')[0] : new Date(doc.expiryDate).toISOString().split('T')[0]) : ''}
                                                        onChange={e => handleDocumentChange(idx, 'expiryDate', e.target.value)}
                                                    />
                                                </div>

                                                {/* Method Selection */}
                                                <div className="md:col-span-3">
                                                    <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1.5">
                                                        <Upload className="h-3.5 w-3.5 text-gray-400" />
                                                        Method
                                                    </label>
                                                    <div className="flex bg-gray-100 p-1 rounded-lg">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDocumentChange(idx, 'documentMethod', 'upload')}
                                                            className={`flex-1 flex items-center justify-center gap-1.5 py-1 text-xs font-medium rounded-md transition-all ${doc.documentMethod === 'upload'
                                                                ? 'bg-white text-agrosaf-green shadow-sm'
                                                                : 'text-gray-500 hover:text-gray-700'
                                                                }`}
                                                        >
                                                            <Upload className="h-3 w-3" /> Upload
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDocumentChange(idx, 'documentMethod', 'link')}
                                                            className={`flex-1 flex items-center justify-center gap-1.5 py-1 text-xs font-medium rounded-md transition-all ${doc.documentMethod === 'link'
                                                                ? 'bg-white text-agrosaf-green shadow-sm'
                                                                : 'text-gray-500 hover:text-gray-700'
                                                                }`}
                                                        >
                                                            <LinkIcon className="h-3 w-3" /> Link
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Upload or Link Area */}
                                                <div className="md:col-span-12 mt-2">
                                                    {doc.documentMethod === 'upload' ? (
                                                        <div
                                                            className={`relative overflow-hidden group/drop border-2 border-dashed rounded-xl transition-all ${draggingDocIdx === idx
                                                                ? 'border-agrosaf-green bg-green-50'
                                                                : doc.fileInfo
                                                                    ? 'border-green-200 bg-green-50/20'
                                                                    : 'border-gray-200 bg-gray-50/30 hover:border-gray-300 hover:bg-gray-50'
                                                                }`}
                                                            style={{ minHeight: 140 }}
                                                            onDragOver={e => handleDragOver(e, idx)}
                                                            onDragLeave={handleDragLeave}
                                                            onDrop={e => handleDrop(e, idx)}
                                                            onClick={() => !doc.uploading && document.getElementById(`doc-file-input-${idx}`)?.click()}
                                                        >
                                                            <input
                                                                id={`doc-file-input-${idx}`}
                                                                type="file"
                                                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv,.txt"
                                                                className="hidden"
                                                                onChange={e => handleDocumentFile(idx, e)}
                                                                disabled={doc.uploading}
                                                            />

                                                            <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center cursor-pointer">
                                                                {doc.uploading ? (
                                                                    <div className="flex flex-col items-center gap-2">
                                                                        <div className="relative h-10 w-10">
                                                                            <div className="absolute inset-0 border-2 border-green-100 rounded-full"></div>
                                                                            <div className="absolute inset-0 border-2 border-agrosaf-green border-t-transparent rounded-full animate-spin"></div>
                                                                        </div>
                                                                        <span className="text-sm font-medium text-agrosaf-green">Uploading...</span>
                                                                    </div>
                                                                ) : doc.fileInfo ? (
                                                                    <div className="flex items-center gap-4 w-full">
                                                                        <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                                            <File className="h-6 w-6 text-agrosaf-green" />
                                                                        </div>
                                                                        <div className="flex-1 text-left min-w-0">
                                                                            <p className="text-sm font-semibold text-gray-900 truncate max-w-[300px]">
                                                                                {doc.fileInfo.name || doc.fileInfo.originalName || 'Document'}
                                                                            </p>
                                                                            <p className="text-xs text-gray-500">
                                                                                {doc.fileInfo.size ? `${(doc.fileInfo.size / (1024 * 1024)).toFixed(2)} MB • ` : ''}
                                                                                {(doc.fileInfo.mimetype || doc.fileInfo.type)?.split('/')[1]?.toUpperCase() || 'FILE'}
                                                                            </p>
                                                                        </div>
                                                                        <div className="flex items-center gap-1">
                                                                            <div className="px-2 py-1 bg-green-100 text-green-700 text-[10px] font-bold rounded uppercase mr-1">Uploaded</div>
                                                                            {(doc.fileInfo?.url || doc.documentLink) && (
                                                                                <a
                                                                                    href={doc.fileInfo?.url || doc.documentLink}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="p-1.5 text-gray-400 hover:text-agrosaf-green transition-colors"
                                                                                    onClick={e => e.stopPropagation()}
                                                                                    title="View Document"
                                                                                >
                                                                                    <Eye className="h-4 w-4" />
                                                                                </a>
                                                                            )}
                                                                            <button
                                                                                type="button"
                                                                                className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                                                                                onClick={e => { e.stopPropagation(); handleDocumentDelete(idx); }}
                                                                                title="Delete Document"
                                                                            >
                                                                                <Trash2 className="h-4 w-4" />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        <div className="mb-2 p-2 bg-white rounded-full shadow-sm border border-gray-100 group-hover/drop:scale-110 transition-transform">
                                                                            <Upload className="h-5 w-5 text-gray-400" />
                                                                        </div>
                                                                        <p className="text-sm font-medium text-gray-700">
                                                                            Click to upload or <span className="text-agrosaf-green">drag and drop</span>
                                                                        </p>
                                                                        <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG, DOC (Max 10MB)</p>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="space-y-1.5">
                                                            <div className="relative">
                                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                                    <LinkIcon className="h-4 w-4 text-gray-400" />
                                                                </div>
                                                                <input
                                                                    type="url"
                                                                    placeholder="https://drive.google.com/..."
                                                                    className="w-full bg-white border border-gray-300 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-green-500/20 focus:border-agrosaf-green outline-none transition-all"
                                                                    value={doc.documentLink || ''}
                                                                    onChange={e => handleDocumentChange(idx, 'documentLink', e.target.value)}
                                                                />
                                                            </div>
                                                            <p className="text-[10px] text-gray-500 ml-1">Paste a link to Google Drive, Dropbox or any public document URL</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Business Information Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                        <Building className="h-5 w-5 text-agrosaf-green" />
                        <h2 className="font-semibold text-gray-900">Business Information</h2>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Website
                                </label>
                                <input
                                    type="url"
                                    name="website"
                                    value={formData.website}
                                    onChange={(e) => handleFieldChange('website', e.target.value, 'url', 100)}
                                    placeholder="https://example.com"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent outline-none transition-all"
                                />
                                <div className="flex justify-end mt-1">
                                    <span className="text-[10px] text-gray-400 font-medium">
                                        {getCharacterCount(formData.website, 100)}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Industry
                                </label>
                                <select
                                    name="industry"
                                    value={formData.industry}
                                    onChange={(e) => handleFieldChange('industry', e.target.value, 'select')}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent outline-none transition-all"
                                >
                                    <option value="">Select Industry</option>
                                    {isLoadingIndustries ? (
                                        <option disabled>Loading...</option>
                                    ) : (
                                        industriesData?.filter(i => i.isActive !== false || i.name === formData.industry).map((industry) => (
                                            <option key={industry._id} value={industry.name}>
                                                {industry.name} {industry.isActive === false ? '(Inactive)' : ''}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Company Size
                                </label>
                                <select
                                    name="companySize"
                                    value={formData.companySize}
                                    onChange={(e) => handleFieldChange('companySize', e.target.value, 'select')}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent outline-none transition-all"
                                >
                                    <option value="">Select Company Size</option>
                                    <option value="Startup (1-10 employees)">Startup (1-10 employees)</option>
                                    <option value="Small (10-50 employees)">Small (10-50 employees)</option>
                                    <option value="Medium (50-200 employees)">Medium (50-200 employees)</option>
                                    <option value="Large (200-1000 employees)">Large (200-1000 employees)</option>
                                    <option value="Enterprise (1000+ employees)">Enterprise (1000+ employees)</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Annual Revenue (₹)
                                </label>
                                <input
                                    type="text"
                                    name="annualRevenue"
                                    value={formData.annualRevenue}
                                    onChange={(e) => handleFieldChange('annualRevenue', e.target.value, 'number', 15)}
                                    placeholder="Enter annual revenue"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent outline-none transition-all"
                                />
                                <div className="flex justify-end mt-1">
                                    <span className="text-[10px] text-gray-400 font-medium">
                                        {getCharacterCount(formData.annualRevenue, 15)}
                                    </span>
                                </div>
                            </div>

                            {!isSalesExecutive && (
                                <div>
                                    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                                        <Users className="h-4 w-4 text-gray-400" />
                                        Assigned To
                                    </label>
                                    <SearchableDropdown
                                        options={assigneeOptions}
                                        value={formData.assignedTo}
                                        onChange={(selectedId) => {
                                            handleFieldChange('assignedTo', selectedId, 'select');
                                            if (selectedId) {
                                                const foundRep = (assigneesData || []).find(r => (r.userId || r.user || r._id) === selectedId);
                                                const foundOption = assigneeOptions.find(opt => opt.id === selectedId);
                                                const repObj = foundRep || foundOption?.rep || foundOption;
                                                if (repObj) {
                                                    setSelectedAssignee(repObj);
                                                }
                                            } else {
                                                setSelectedAssignee(null);
                                            }
                                        }}
                                        onSearch={(searchTerm) => {
                                            setAssigneeSearchTerm(searchTerm);
                                        }}
                                        placeholder={loadingAssignees ? 'Loading...' : 'Select Employee'}
                                        loading={loadingAssignees}
                                        searchPlaceholder="Search by name or email..."
                                        minSearchLength={2}
                                    />
                                </div>
                            )}

                            {/* Removed GST Number, PAN Card Number, Drug License Number, Drug License Expiry fields as per new requirements */}
                        </div>
                    </div>
                </div >

                {/* Additional Details Section */}
                < div className="bg-white rounded-xl shadow-sm border border-gray-200" >
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                        <FileText className="h-5 w-5 text-agrosaf-green" />
                        <h2 className="font-semibold text-gray-900">Additional Details</h2>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Region
                                </label>
                                <select
                                    name="region"
                                    value={formData.region}
                                    onChange={(e) => handleFieldChange('region', e.target.value, 'select')}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent outline-none transition-all"
                                >
                                    <option value="">Select Region</option>
                                    <option value="North">North</option>
                                    <option value="South">South</option>
                                    <option value="East">East</option>
                                    <option value="West">West</option>
                                    <option value="Central">Central</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Decision Maker
                                </label>
                                <select
                                    name="decisionMaker"
                                    value={formData.decisionMaker}
                                    onChange={(e) => handleFieldChange('decisionMaker', e.target.value, 'select')}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent outline-none transition-all"
                                >
                                    <option value="">Select Decision Maker</option>
                                    {isLoadingDecisionMakers ? (
                                        <option disabled>Loading...</option>
                                    ) : (
                                        decisionMakersData?.filter(dm => dm.isActive !== false || dm.name === formData.decisionMaker).map((dm) => (
                                            <option key={dm._id} value={dm.name}>
                                                {dm.name} {dm.isActive === false ? '(Inactive)' : ''}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>
                        </div>
                    </div>
                </div >

                {/* Address Section */}
                < div className="grid grid-cols-1 md:grid-cols-2 gap-6" >
                    {/* Billing Address */}
                    < div className="bg-white rounded-xl shadow-sm border border-gray-200" >
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                            <MapPin className="h-5 w-5 text-agrosaf-green" />
                            <h2 className="font-semibold text-gray-900">Billing Address</h2>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Street Address
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="billingAddress.street"
                                            value={formData.billingAddress.street}
                                            onChange={(e) => handleFieldChange('billingAddress.street', e.target.value, 'address', 100)}
                                            placeholder="Street, building, area"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Country
                                    </label>
                                    <LocationSelect
                                        options={billingCountryOptions}
                                        value={formData.billingAddress.country}
                                        onChange={(country) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                billingAddress: {
                                                    ...prev.billingAddress,
                                                    country: country,
                                                    state: null,
                                                    city: null
                                                }
                                            }));
                                        }}
                                        placeholder="Select Country"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        State
                                    </label>
                                    <LocationSelect
                                        options={billingStateOptions}
                                        value={formData.billingAddress.state}
                                        disabled={!formData.billingAddress.country}
                                        onChange={(state) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                billingAddress: {
                                                    ...prev.billingAddress,
                                                    state: state,
                                                    city: null
                                                }
                                            }));
                                        }}
                                        placeholder="Select State"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        City
                                    </label>
                                    <LocationSelect
                                        options={billingCityOptions}
                                        value={formData.billingAddress.city}
                                        disabled={!formData.billingAddress.state}
                                        onChange={(city) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                billingAddress: {
                                                    ...prev.billingAddress,
                                                    city: city
                                                }
                                            }));
                                        }}
                                        placeholder="Select City"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Postal Code
                                    </label>
                                    <input
                                        type="text"
                                        name="billingAddress.pincode"
                                        value={formData.billingAddress.pincode}
                                        onChange={(e) => handleFieldChange('billingAddress.pincode', e.target.value, 'text', 15)}
                                        placeholder="Postal Code"
                                        maxLength={15}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                    />
                                    <div className="flex justify-end mt-1">
                                        <p className="text-xs text-gray-500">
                                            {(formData.billingAddress.pincode || '').length}/15 chars
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Shipping Address */}
                    < div className="bg-white rounded-xl shadow-sm border border-gray-200" >
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <MapPin className="h-5 w-5 text-agrosaf-green" />
                                <h2 className="font-semibold text-gray-900">Shipping Address</h2>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={shippingSameAsBilling}
                                    onChange={(e) => setShippingSameAsBilling(e.target.checked)}
                                    className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green"
                                />
                                <span className="text-xs font-medium text-gray-600">Copy from billing address</span>
                            </label>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Street Address
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="shippingAddress.street"
                                            value={formData.shippingAddress.street}
                                            onChange={(e) => handleFieldChange('shippingAddress.street', e.target.value, 'address', 100)}
                                            placeholder="Street, building, area"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Country
                                    </label>
                                    <LocationSelect
                                        options={shippingCountryOptions}
                                        value={formData.shippingAddress.country}
                                        onChange={(country) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                shippingAddress: {
                                                    ...prev.shippingAddress,
                                                    country: country,
                                                    state: null,
                                                    city: null
                                                }
                                            }));
                                        }}
                                        placeholder="Select Country"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        State
                                    </label>
                                    <LocationSelect
                                        options={shippingStateOptions}
                                        value={formData.shippingAddress.state}
                                        disabled={!formData.shippingAddress.country}
                                        onChange={(state) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                shippingAddress: {
                                                    ...prev.shippingAddress,
                                                    state: state,
                                                    city: null
                                                }
                                            }));
                                        }}
                                        placeholder="Select State"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        City
                                    </label>
                                    <LocationSelect
                                        options={shippingCityOptions}
                                        value={formData.shippingAddress.city}
                                        disabled={!formData.shippingAddress.state}
                                        onChange={(city) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                shippingAddress: {
                                                    ...prev.shippingAddress,
                                                    city: city
                                                }
                                            }));
                                        }}
                                        placeholder="Select City"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Postal Code
                                    </label>
                                    <input
                                        type="text"
                                        name="shippingAddress.pincode"
                                        value={formData.shippingAddress.pincode}
                                        onChange={(e) => handleFieldChange('shippingAddress.pincode', e.target.value, 'text', 15)}
                                        placeholder="Postal Code"
                                        maxLength={15}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                    />
                                    <div className="flex justify-end mt-1">
                                        <p className="text-xs text-gray-500">
                                            {(formData.shippingAddress.pincode || '').length}/15 chars
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-4 pt-4">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => navigate(-1)}
                        className="px-6"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        loading={isSubmitting}
                        icon={<Save className="h-4 w-4" />}
                        className="px-8 bg-agrosaf-green hover:bg-agrosaf-green-hover text-white rounded-lg py-2 shadow-md transition-all active:scale-95"
                    >
                        {isEditMode ? 'Update Customer' : 'Create Customer'}
                    </Button>
                </div>
            </form >

            {/* Duplicate Warning Modal */}
            {duplicateWarning && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="bg-amber-50 px-6 py-4 flex items-center gap-3 border-b border-amber-100">
                            <div className="bg-amber-100 p-2 rounded-full">
                                <AlertTriangle className="h-6 w-6 text-amber-600" />
                            </div>
                            <h3 className="text-lg font-bold text-amber-900">Duplicate Customer Found</h3>
                        </div>

                        <div className="p-6">
                            <p className="text-gray-600 mb-4">
                                A customer already exists with this <span className="font-semibold text-gray-900">{duplicateWarning.field}</span>:
                                <span className="block mt-1 font-mono text-agrosaf-green bg-gray-50 px-2 py-1 rounded inline-block">
                                    {duplicateWarning.value}
                                </span>
                            </p>

                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-3">
                                <div className="flex justify-between items-start">
                                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Customer Details</span>
                                    <span className="bg-agrosaf-green/10 text-agrosaf-green text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">
                                        Active
                                    </span>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex items-center gap-3">
                                        <div className="bg-white p-1.5 rounded-lg border border-gray-200">
                                            <Hash className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-gray-400 font-medium leading-none mb-1">Customer ID</p>
                                            <p className="text-sm font-bold text-gray-900 leading-none">{duplicateWarning.customer?.customerId}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <div className="bg-white p-1.5 rounded-lg border border-gray-200">
                                            <User className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-gray-400 font-medium leading-none mb-1">Contact Person</p>
                                            <p className="text-sm font-bold text-gray-900 leading-none">{duplicateWarning.customer?.contactPerson}</p>
                                        </div>
                                    </div>

                                    {duplicateWarning.customer?.companyName && (
                                        <div className="flex items-center gap-3">
                                            <div className="bg-white p-1.5 rounded-lg border border-gray-200">
                                                <Building className="h-4 w-4 text-gray-400" />
                                            </div>
                                            <div>
                                                <p className="text-[10px] text-gray-400 font-medium leading-none mb-1">Company Name</p>
                                                <p className="text-sm font-bold text-gray-900 leading-none">{duplicateWarning.customer?.companyName}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <p className="mt-4 text-sm text-amber-700 font-medium italic">
                                You can still proceed with creation if this is a separate record.
                            </p>
                        </div>

                        <div className="px-6 py-4 bg-gray-50 flex gap-3">
                            <button
                                onClick={() => setDuplicateWarning(null)}
                                className="flex-1 px-4 py-2 bg-white border border-amber-200 text-amber-700 font-semibold rounded-xl hover:bg-amber-50 transition-colors shadow-sm"
                            >
                                Create Anyway
                            </button>
                            <button
                                onClick={() => {
                                    setDuplicateWarning(null);
                                    window.open(`/customer_360?customerId=${duplicateWarning.customer?.customerId}`, '_blank');
                                }}
                                className="flex-1 px-4 py-2 bg-agrosaf-green text-white font-semibold rounded-xl hover:bg-agrosaf-green-hover transition-all shadow-md flex items-center justify-center gap-2"
                            >
                                <ExternalLink className="h-4 w-4" />
                                View Existing
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default CustomerForm;
